/**
 * Log in / create an account in a pop-up over the page you are on (Stage 8, Sam 2026-10-23: the separate login page "has
 * a totally different feel"). Centred, the Prii logo and an × on top (no title or line under it: Sam, 2026-10-23 - "Join
 * Darker Waves" read oddly over a login form), a Log in | Sign up switch under it. It opens on Create account on a phone nobody has signed in on here, on Log in otherwise. The × cancels: what you
 * tapped does not happen (its stashed action is dropped). Signed in, the pop-up closes and the page finishes what you
 * started - the event page's and group page's own pending-action effects run as the session arrives.
 *
 *   const promptAuth = usePromptAuth();
 *   stashPendingAction({ type: 'rsvp_going', eventId }); promptAuth({ action: 'Join', target: 'Darker Waves' });
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal, { ModalBody, ModalHeader } from '../ui/Modal';
import PriiLogo from '../PriiLogo';
import Button from '../ui/Button';
import TextInput from '../ui/TextInput';
import PasswordInput from '../ui/PasswordInput';
import PasswordRules from '../ui/PasswordRules';
import { EmailField } from '../Auth';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { clearPendingAction } from '../../lib/pendingAction';
import { hasAccountHere, markHasAccountHere, submitAuth } from '../../lib/auth/submitAuth';
import { isCancelled, signInWithProvider } from '../../lib/auth/socialSignIn';
import { socialSignInShown } from '../../config/socialSignIn';

/** Apple's and Google's own marks (their sign-in buttons must carry them). */
function AppleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.37 12.37c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.59 2.18-1.53 2.66-.39 6.6 1.1 8.75.73 1.05 1.6 2.24 2.73 2.2 1.1-.05 1.51-.71 2.84-.71 1.32 0 1.7.71 2.85.69 1.18-.02 1.93-1.07 2.65-2.13.83-1.22 1.18-2.4 1.2-2.46-.03-.01-2.3-.88-2.32-3.52zM14.2 5.9c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.69-.92 2.69.97.07 1.96-.49 2.56-1.24z" />
    </svg>
  );
}
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

const AuthPromptContext = createContext(() => {});
export function usePromptAuth() {
  return useContext(AuthPromptContext);
}

export function AuthPromptProvider({ children }) {
  const { session } = useCurrentUserProfile();
  const [ask, setAsk] = useState(null); // { action, target, mode, onSuccess, key }
  // Anyone signed in on this phone makes it a "signed in here before" phone (today's users included, not only new sign-ins).
  useEffect(() => {
    if (session?.user?.id) markHasAccountHere();
  }, [session?.user?.id]);
  const promptAuth = useCallback((opts = {}) => {
    setAsk({
      action: opts.action ?? 'Log in',
      target: opts.target ?? '',
      mode: opts.mode ?? (hasAccountHere() ? 'login' : 'signup'),
      onSuccess: opts.onSuccess ?? null,
      key: Date.now(),
    });
  }, []);
  // A dev build: ?authprompt=signup|login&authfor=<name>&authdo=Join opens it over any page (the Stage 8 mockups).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const q = new URLSearchParams(window.location.search);
    const m = q.get('authprompt');
    if (m)
      setAsk({
        action: q.get('authdo') || 'Join',
        target: q.get('authfor') || '',
        mode: m,
        onSuccess: null,
        key: 1,
      });
  }, []);
  const cancel = useCallback(() => {
    clearPendingAction();
    setAsk(null);
  }, []);
  const done = useCallback(() => {
    const fn = ask?.onSuccess;
    setAsk(null);
    fn?.();
  }, [ask]);
  return (
    <AuthPromptContext.Provider value={promptAuth}>
      {children}
      {ask && (
        <AuthModal
          key={ask.key}
          action={ask.action}
          target={ask.target}
          mode={ask.mode}
          onCancel={cancel}
          onDone={done}
        />
      )}
    </AuthPromptContext.Provider>
  );
}

function AuthModal({ action, target, mode, onCancel, onDone }) {
  const navigate = useNavigate();
  const [signUp, setSignUp] = useState(mode === 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setError('');
  }, [email, password, name, signUp]);
  // Not shown (the logo is the header); a screen reader still hears what the pop-up is for.
  const label = useMemo(
    () => (target ? `Log in or sign up to ${action.toLowerCase()} ${target}` : 'Log in or sign up'),
    [action, target],
  );

  // Apple and Google above the email form, no "or" line (Sam, 2026-10-26); off until the setup is done
  // (config/socialSignIn). A cancelled sheet leaves the pop-up as it was.
  const social = socialSignInShown();
  const withProvider = async (provider) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const session = await signInWithProvider(provider);
      if (session) onDone(); // the web build is redirecting instead; it comes back signed in
    } catch (err) {
      if (!isCancelled(err)) setError('Could not sign in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const r = await submitAuth({ isSignUp: signUp, email, password, name });
    setBusy(false);
    if (r.error) {
      if (r.field) setTouched(true);
      setError(r.error);
      return;
    }
    onDone();
  };

  return (
    <Modal isOpen onClose={onCancel} size="sm" keyboardAware closeOnVeil={false}>
      <ModalHeader onClose={onCancel}>
        <h2 className="sr-only">{label}</h2>
        <div className="flex justify-center pl-9">
          <PriiLogo size="sm" bare wordmark />
        </div>
      </ModalHeader>
      <ModalBody>
        {social && (
          <div className="mb-5 flex flex-col gap-2">
            <button
              type="button"
              className="auth-social auth-social--apple"
              onClick={() => withProvider('apple')}
              disabled={busy}
            >
              <AppleMark /> Continue with Apple
            </button>
            <button
              type="button"
              className="auth-social auth-social--google"
              onClick={() => withProvider('google')}
              disabled={busy}
            >
              <GoogleMark /> Continue with Google
            </button>
          </div>
        )}
        <div className="mb-5 grid grid-cols-2 gap-1 rounded-btn bg-white/5 p-1" role="tablist">
          {[
            ['Log in', false],
            ['Sign up', true],
          ].map(([label, su]) => (
            <Button
              key={label}
              type="button"
              role="tab"
              aria-selected={signUp === su}
              variant={signUp === su ? 'chipSelected' : 'ghost'}
              size="md"
              onClick={() => setSignUp(su)}
              disabled={busy}
            >
              {label}
            </Button>
          ))}
        </div>
        <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
          {signUp && (
            <TextInput
              type="text"
              name="name"
              placeholder="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="nickname"
              maxLength={40}
              disabled={busy}
            />
          )}
          <EmailField
            name="email"
            value={email}
            onChange={setEmail}
            onBlur={() => email && setTouched(true)}
            showError={touched}
            autoComplete={signUp ? 'email' : 'username'}
            disabled={busy}
          />
          <PasswordInput
            name="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={signUp ? 'new-password' : 'current-password'}
            visible={show}
            onToggleVisible={() => setShow((v) => !v)}
            disabled={busy}
          />
          {signUp ? (
            <PasswordRules password={password} />
          ) : (
            <div className="-mt-1 flex justify-end">
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline"
                onClick={() => {
                  onCancel();
                  navigate('/login', { state: { forgot: true, email } });
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          {error && <p className="text-center text-sm text-danger">{error}</p>}
          <Button type="submit" size="lg" fullWidth loading={busy}>
            {signUp ? 'Create account' : 'Log in'}
          </Button>
        </form>
      </ModalBody>
    </Modal>
  );
}
